"use client";

import {
  CopyButton,
  DataTable,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { JournalMetricsLookupStrings } from "@/components/tools/journal-metrics-lookup";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type JournalMetrics,
  oaShare,
  recentYears,
  yearOaShare,
} from "@/lib/tool-kit/research-discovery/journal-metrics";

type Fmt = ReturnType<typeof useToolNumber>;

const LINK = "break-all text-primary underline-offset-4 hover:underline";

function regionName(code: string, locale: string): string {
  if (code === "") {
    return "";
  }
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

function money(price: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(price);
  } catch {
    return `${price} ${currency}`;
  }
}

function apcText(
  journal: JournalMetrics,
  strings: JournalMetricsLookupStrings,
  locale: string
): string {
  if (journal.apcs.length === 0) {
    return strings.apcNone;
  }
  return journal.apcs
    .slice(0, 3)
    .map((apc) => money(apc.price, apc.currency, locale))
    .join(" · ");
}

function yesNo(value: boolean, strings: JournalMetricsLookupStrings): string {
  return value ? strings.yes : strings.no;
}

function accessRows(
  journal: JournalMetrics,
  strings: JournalMetricsLookupStrings,
  locale: string
): [string, string][] {
  return [
    [strings.fullyOa, yesNo(journal.fullyOa, strings)],
    [
      strings.inDoaj,
      journal.inDoaj && journal.doajSince
        ? t(strings.doajSince, { year: String(journal.doajSince) })
        : yesNo(journal.inDoaj, strings),
    ],
    [strings.highOaRate, yesNo(journal.highOaRate, strings)],
    [strings.apc, apcText(journal, strings, locale)],
    [strings.inMedline, yesNo(journal.inMedline, strings)],
    [strings.inCore, yesNo(journal.inCore, strings)],
  ];
}

export function summaryText(
  journal: JournalMetrics,
  strings: JournalMetricsLookupStrings,
  fmt: Fmt
): string {
  const share = oaShare(journal);
  return [
    journal.issnL
      ? `${journal.name} (${strings.issnL} ${journal.issnL})`
      : journal.name,
    `${strings.works}: ${fmt(journal.works)}`,
    `${strings.citations}: ${fmt(journal.citations)}`,
    journal.hIndex === null ? "" : `${strings.hIndex}: ${fmt(journal.hIndex)}`,
    journal.meanCitedness === null
      ? ""
      : `${strings.meanCitedness}: ${fmt(journal.meanCitedness, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}`,
    share === null
      ? ""
      : `${strings.oaShare}: ${fmt(share, { maximumFractionDigits: 1 })}%`,
    strings.summarySource,
  ]
    .filter((line) => line !== "")
    .join("\n");
}

export function JournalCard({
  journal,
  strings,
  lookupYear,
}: {
  journal: JournalMetrics;
  strings: JournalMetricsLookupStrings;
  lookupYear: number;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const share = oaShare(journal);
  const country = regionName(journal.country, locale);
  const typeName =
    (strings.types as Record<string, string>)[journal.type] ?? journal.type;
  const meta = [typeName, journal.publisher, country]
    .filter((part) => part !== "")
    .join(" · ");
  const active =
    journal.firstYear && journal.lastYear
      ? t(strings.activeYears, {
          first: String(journal.firstYear),
          last: String(journal.lastYear),
        })
      : "";
  const rows = recentYears(journal).map((row) => {
    const rowShare = yearOaShare(row);
    return [
      row.year === lookupYear
        ? t(strings.yearToDate, { year: String(row.year) })
        : String(row.year),
      fmt(row.works),
      rowShare === null
        ? "–"
        : `${fmt(rowShare, { maximumFractionDigits: 0 })}%`,
      fmt(row.citations),
    ];
  });

  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <h3 className={WIDGET_H3}>{journal.name}</h3>
        <p className="text-muted-foreground text-sm">{meta}</p>
        <p className="text-muted-foreground text-sm">
          {[
            journal.issnL ? `${strings.issnL} ${journal.issnL}` : "",
            journal.issns.length > 1 ? journal.issns.join(", ") : "",
            active,
          ]
            .filter((part) => part !== "")
            .join(" · ")}
        </p>
        {journal.alternateTitles.length > 0 ? (
          <p className={HINT}>
            {strings.alsoKnown} {journal.alternateTitles.join("; ")}
          </p>
        ) : null}
      </div>
      <StatGrid>
        <StatCard label={strings.works} value={fmt(journal.works)} />
        <StatCard label={strings.citations} value={fmt(journal.citations)} />
        <StatCard
          label={strings.hIndex}
          value={journal.hIndex === null ? "–" : fmt(journal.hIndex)}
        />
        <StatCard
          label={strings.i10Index}
          value={journal.i10Index === null ? "–" : fmt(journal.i10Index)}
        />
        <StatCard
          emphasis
          label={strings.meanCitedness}
          value={
            journal.meanCitedness === null
              ? "–"
              : fmt(journal.meanCitedness, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })
          }
        />
        <StatCard
          label={strings.oaShare}
          value={
            share === null
              ? "–"
              : `${fmt(share, { maximumFractionDigits: 1 })}%`
          }
        />
      </StatGrid>
      <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-foreground text-sm">
        {strings.notJif}
      </p>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.accessHeading}</h3>
        <DataTable
          head={[strings.colFeature, strings.colValue]}
          rows={accessRows(journal, strings, locale)}
        />
        <p className={HINT}>{strings.apcNote}</p>
      </div>
      {rows.length > 0 ? (
        <div className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.yearsHeading}</h3>
          <DataTable
            head={[
              strings.colYear,
              strings.colWorks,
              strings.colOaShare,
              strings.colCitations,
            ]}
            rows={rows}
          />
          <p className={HINT}>{strings.yearsNote}</p>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copySummary}
          text={summaryText(journal, strings, fmt)}
        />
        <a
          className={LINK}
          href={`https://openalex.org/${journal.id}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {strings.openAlexLink}
        </a>
        {journal.homepage ? (
          <a
            className={LINK}
            href={journal.homepage}
            rel="noopener noreferrer"
            target="_blank"
          >
            {strings.homepageLink}
          </a>
        ) : null}
      </div>
    </div>
  );
}
